import { type ReactNode, useEffect, useRef, useState } from "react";
import { type AiConnection, api, type Me, type TripMark } from "./api.ts";
import { ConnectScreen } from "./Connect.tsx";
import { HowItWorks } from "./HowItWorks.tsx";
import { usePersonEvents } from "./me-live.ts";
import { navigate, tripPath } from "./router.ts";
import { Button, Icon } from "./souvenir.ts";
import { Wordmark } from "./ui.tsx";

// Getting started (features/getting-started.md; DESIGN.md, "Getting started"): signed in with no AI
// connected, every page is this. How Tripmo works (once per person), then Connect your AI, then
// Connected: on every page of theirs that's open, the moment they allow an AI on Tripmo's page (or it
// first calls), whichever page they're on. It stays until tapped: Start your first trip, or, for someone
// who came from a trip's join link, Open <trip> once they're let in. Nothing moves on by itself.
// Connecting can be skipped for now (Skip for now, kept for the account): every page is then as for anyone,
// and the profile's Connect your AI opens this again at connecting (`again`), Connected as ever after it.

// The trip someone asked to join from its link, while they connect.
export interface Joining {
  id: string;
  title: string;
  owner: string;
  // No longer asking (turned down, or taken back on another device): the join page says which.
  onGone?: () => void;
}

type Step = "how" | "connect" | "connected";

// The newest of my AIs: the one that just connected.
const newest = (ais: AiConnection[]) =>
  ais.reduce<AiConnection | null>((a, b) => (a && a.first_at >= b.first_at ? a : b), null);

export function GettingStarted({
  me,
  joining,
  again,
  onConnected,
  onFinish,
}: {
  me: Me;
  joining?: Joining;
  // Connecting later, from the profile, after skipping it: straight to connecting, Your profile closing it,
  // and once connected, the first trip offered only to someone who has none (`firstTrip`).
  again?: { firstTrip: boolean; onClose: () => void };
  // An AI just connected, heard here: the page keeps getting started until it's tapped through.
  onConnected: () => void;
  // Tapped through (Start your first trip, Your trips, or Open <trip>), or connecting skipped for now
  // (kept for the account first): the page is on to the app, where it was going.
  onFinish: () => void;
}) {
  const [ai, setAi] = useState<AiConnection | null>(() => newest(me.ais));
  const [step, setStep] = useState<Step>(() =>
    ai ? "connected" : again || me.tips_seen.includes("how_tripmo_works") ? "connect" : "how",
  );
  const [skipping, setSkipping] = useState(false);
  // How Tripmo works again, opened from the connect screen.
  const [replay, setReplay] = useState(false);
  const [check, setCheck] = useState<{ busy: boolean; result: "not_yet" | string | null }>({
    busy: false,
    result: null,
  });
  // A joiner: whether they're in the trip yet (the owner may let them in at any moment).
  const [letIn, setLetIn] = useState(false);

  const heard = (ais: AiConnection[]) => {
    const first = newest(ais);
    if (!first || ai) return;
    setAi(first);
    setStep("connected");
    setReplay(false);
    onConnected();
  };
  usePersonEvents(
    {
      ais: heard,
      trips: ({ trips }: { trips: TripMark[] }) => {
        setLetIn(Boolean(joining && trips.some((t) => t.id === joining.id && t.status === "member")));
        if (joining && !trips.some((t) => t.id === joining.id)) joining.onGone?.();
      },
    },
    ai === null || (joining !== undefined && !letIn),
  );

  const checkAgain = async () => {
    setCheck({ busy: true, result: null });
    try {
      const { ais } = await api.ais();
      heard(ais);
      setCheck({ busy: false, result: ais.length ? null : "not_yet" });
    } catch (e) {
      setCheck({ busy: false, result: (e as Error).message });
    }
  };

  // Skip for now: kept for the account, so no device asks again, then on to where they were going (the
  // page they're on: Your trips, a trip; for a joiner, waiting to be let in, then the trip). If it doesn't
  // reach Tripmo, this page still goes on, and getting started shows once more next time.
  const skip = async () => {
    setSkipping(true);
    await api.putTipAway("skip_connecting").catch(() => {});
    onFinish();
  };

  const seenHow = () => {
    setStep("connect");
    // Seen once, on every device; if it doesn't reach Tripmo, it shows once more next time, no harm.
    api.putTipAway("how_tripmo_works").catch(() => {});
  };

  let body: ReactNode;
  if (step === "connected" && ai)
    body = (
      <Connected
        ai={ai}
        {...(joining ? { joining } : {})}
        firstTrip={again?.firstTrip ?? true}
        letIn={letIn}
        onStart={() => {
          navigate("/");
          onFinish();
        }}
        onOpen={() => {
          if (!joining) return;
          navigate(tripPath(joining.id));
          onFinish();
        }}
      />
    );
  else if (step === "how" || replay)
    body = (
      <HowItWorks
        last="Connect my AI"
        onDone={() => {
          setReplay(false);
          seenHow();
        }}
        {...(joining && !replay
          ? { note: `You've asked to join. While ${joining.owner} decides, here's how Tripmo works.` }
          : {})}
      />
    );
  else
    body = (
      <ConnectScreen
        me={me}
        {...(joining ? { joining: { title: joining.title, owner: joining.owner } } : {})}
        check={check}
        onCheck={checkAgain}
        onHow={() => setReplay(true)}
        {...(again ? {} : { skip: { busy: skipping, run: skip } })}
      />
    );

  return (
    <div className="tm-page tm-start-page">
      <header className="tm-bar">
        <Wordmark />
        <span className="tm-grow" />
        {again ? (
          <Button variant="quiet" size="sm" onClick={again.onClose}>
            Your profile
          </Button>
        ) : (
          <Button variant="quiet" size="sm" onClick={() => api.signOut()}>
            Sign out
          </Button>
        )}
      </header>
      {body}
    </div>
  );
}

// Connected, unmistakably: a big check, which AI, and the one thing to do next: the first trip, or for
// someone with trips already (connecting later, after skipping), Your trips. For a joiner, the trip once
// they're let in, or who they're waiting for.
function Connected({
  ai,
  joining,
  firstTrip,
  letIn,
  onStart,
  onOpen,
}: {
  ai: AiConnection;
  joining?: Joining;
  firstTrip: boolean;
  letIn: boolean;
  onStart: () => void;
  onOpen: () => void;
}) {
  const name = ai.name ?? "Your AI";
  // Read out where the person is looking: the screen changed under them, by itself.
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);
  useEffect(() => {
    const before = document.title;
    document.title = `${name} is connected · Tripmo`;
    return () => {
      document.title = before;
    };
  }, [name]);
  return (
    <main className="tm-connected">
      <span className="tm-connected-mark" aria-hidden="true">
        <Icon name="check" size={40} strokeWidth={2.25} />
      </span>
      <h1 className="display-l" ref={heading} tabIndex={-1}>
        {name} is connected to Tripmo
      </h1>
      {joining ? (
        <>
          <p className="body tm-muted">It's ready to plan {joining.title} with you.</p>
          {letIn ? (
            <Button variant="primary" block onClick={onOpen}>
              Open {joining.title}
            </Button>
          ) : (
            <p className="tm-connected-wait" aria-live="polite">
              <span className="tm-wait-dot" aria-hidden="true" />
              <span className="body">Waiting for {joining.owner} to let you in</span>
            </p>
          )}
        </>
      ) : (
        <>
          <p className="body tm-muted">It can plan your trips now. Everything it saves shows up here.</p>
          <Button variant="primary" block onClick={onStart}>
            {firstTrip ? "Start your first trip" : "Your trips"}
          </Button>
        </>
      )}
    </main>
  );
}
